import { useEffect, useMemo, useState } from "react";
import { theme } from "../styles/theme";
import { normalizarNombreDocente } from "../utils/docenteNombre";
import { normalizarAnioRegistro, normalizarRegistroDesdeSheet } from "../utils/registroSchema";

const CAMPOS_FORMULARIO = [
  "anio",
  "maestria",
  "fecha_inicio",
  "fecha_fin",
  "cohorte",
  "cedula",
  "estudiante",
  "tema",
  "director",
  "estado_contratacion_director",
  "tutor",
  "cotutor",
  "evaluador",
  "estado_contratacion_evaluador",
  "evaluador1",
  "evaluador2",
  "fecha_graduacion",
  "examen",
  "resol_ca",
  "fecha_resol_ca",
  "resol_cua",
  "fecha_resol_cua",
  "fecha_mat2",
  "prorroga1",
  "prorroga2",
  "mat_estado",
  "estado",
  "fecha_tentativa",
  "periodo_inicio",
  "pendiente_actualizacion",
  "materia_reprobada",
  "deudas"
];

const CAMPOS_TEXTAREA = new Set(["tema", "materia_reprobada", "deudas"]);
const CAMPOS_DOCENTE = new Set([
  "director",
  "tutor",
  "cotutor",
  "evaluador",
  "evaluador1",
  "evaluador2"
]);
const CAMPOS_OBLIGATORIOS = new Set([
  "anio",
  "maestria",
  "cohorte",
  "estudiante",
  "tema",
  "director",
  "evaluador",
  "examen",
  "estado"
]);
const MAESTRIAS_DISPONIBLES = ["MET", "MCD", "MACI", "MIB", "MSEP", "MSI", "MSIG", "MCC", "DCCA", "DIE"];
const COHORTES_DISPONIBLES = Array.from({ length: 26 }, (_, index) => String(index + 1));
const ANIOS_DISPONIBLES = ["2020", "2021", "2022", "2023", "2024", "2025", "2026", "2027", "2028", "2029"];
const ANIO_ACTUAL = String(new Date().getFullYear());
const ANIO_POR_DEFECTO = ANIOS_DISPONIBLES.includes(ANIO_ACTUAL) ? ANIO_ACTUAL : ANIOS_DISPONIBLES[0];
const EXAMEN_OPTIONS = ["SI", "NO"];
const ESTADO_OPTIONS = ["GRADUADO", "PROPUESTA DE TEMAS Y TEMARIOS"];
const ESTADOS_CONTRATACION_OPTIONS = ["CONTRATADO", "EN PROCESO DE CONTRATACION"];
const SI_NO_OPTIONS = ["SI", "NO"];
const MAESTRIAS_ROLES_EXTENDIDOS = new Set(["MCC", "DIE", "DCCA"]);
const crearEstadoInicial = () =>
  CAMPOS_FORMULARIO.reduce((acc, campo) => {
    acc[campo] = campo === "anio" ? ANIO_POR_DEFECTO : "";
    return acc;
  }, {});

const normalizarInitialData = (initialData) => {
  if (!initialData) {
    return {};
  }

  const data = normalizarRegistroDesdeSheet(initialData);

  if (data.anio) {
    data.anio = normalizarAnioRegistro(data.anio);
  }

  [
    ...CAMPOS_DOCENTE,
    "maestria",
    "examen",
    "estado",
    "estado_contratacion_director",
    "estado_contratacion_evaluador"
  ].forEach((campo) => {
    if (data[campo]) {
      data[campo] = CAMPOS_DOCENTE.has(campo)
        ? normalizarNombreDocente(data[campo])
        : String(data[campo]).trim().toUpperCase();
    }
  });

  if (data.cohorte) {
    const cohorteNormalizada = String(data.cohorte).trim();
    const coincidenciaCohorte = cohorteNormalizada.match(/\d+/);
    data.cohorte = coincidenciaCohorte ? coincidenciaCohorte[0] : cohorteNormalizada;
  }

  if (data.cedula) {
    data.cedula = String(data.cedula).trim();
  }

  return data;
};

const obtenerEtiqueta = (campo) =>
  campo === "anio"
    ? "A\u00f1o de la propuesta de temas y temarios"
    : campo === "examen"
    ? "Examen Complexivo"
    : campo === "fecha_inicio"
      ? "Fecha de inicio de maestria"
      : campo === "fecha_fin"
        ? "Fecha fin de maestria"
    : campo === "director"
      ? "Tutor"
    : campo === "tutor"
      ? "Tutor 2"
    : campo === "resol_ca"
      ? "Resolucion Comite Academico"
    : campo === "fecha_resol_ca"
      ? "Fecha de resolucion de Comite Academico"
    : campo === "resol_cua"
      ? "Resolucion CUA"
    : campo === "fecha_resol_cua"
      ? "Fecha de resolucion CUA"
    : campo === "estado_contratacion_director"
      ? "Estado Contratacion Director"
      : campo === "estado_contratacion_evaluador"
        ? "Estado Contratacion Evaluador"
    : campo === "periodo_inicio"
      ? "Periodo de inicio mat. de titulacion 2"
    : campo === "pendiente_actualizacion"
      ? "Pendiente de actualizacion"
    : campo === "fecha_mat2"
      ? "Fecha de materia de titulacion 2"
    : campo === "prorroga1"
      ? "Prorroga 1"
    : campo === "prorroga2"
      ? "Prorroga 2"
    : campo === "mat_estado"
      ? "Materia de actualizacion"
    : campo === "fecha_tentativa"
      ? "Fecha tentativa de graduacion"
    : campo
        .split("_")
        .map((parte) => parte.charAt(0).toUpperCase() + parte.slice(1))
        .join(" ");

function ModalFormulario({
  onClose,
  onSubmit,
  isSubmitting,
  docentesDisponibles = [],
  initialData = null,
  modo = "crear"
}) {
  const [formData, setFormData] = useState(() => ({
    ...crearEstadoInicial(),
    ...normalizarInitialData(initialData)
  }));
  const [errores, setErrores] = useState({});
  const [cantidadEstudiantes, setCantidadEstudiantes] = useState("1");
  const [estudianteSecundario, setEstudianteSecundario] = useState("");
  const [cedulaSecundaria, setCedulaSecundaria] = useState("");
  const [cohorteSecundaria, setCohorteSecundaria] = useState("");
  const [fechaInicioSecundaria, setFechaInicioSecundaria] = useState("");
  const [fechaFinSecundaria, setFechaFinSecundaria] = useState("");

  useEffect(() => {
    setFormData({
      ...crearEstadoInicial(),
      ...normalizarInitialData(initialData)
    });
    setErrores({});
    setCantidadEstudiantes("1");
    setEstudianteSecundario("");
    setCedulaSecundaria("");
    setCohorteSecundaria("");
    setFechaInicioSecundaria("");
    setFechaFinSecundaria("");
  }, [initialData, modo]);

  const campos = useMemo(
    () =>
      CAMPOS_FORMULARIO.map((campo) => ({
        id: campo,
        label: obtenerEtiqueta(campo),
        type: campo.startsWith("fecha_") ? "date" : "text",
        multiline: CAMPOS_TEXTAREA.has(campo),
        isDocente: CAMPOS_DOCENTE.has(campo),
        required: CAMPOS_OBLIGATORIOS.has(campo),
        options:
          campo === "anio"
            ? ANIOS_DISPONIBLES
            : campo === "maestria"
            ? MAESTRIAS_DISPONIBLES
            : campo === "cohorte"
              ? COHORTES_DISPONIBLES
              : campo === "examen"
                ? EXAMEN_OPTIONS
              : campo === "prorroga1" || campo === "prorroga2"
                ? SI_NO_OPTIONS
              : campo === "pendiente_actualizacion"
                ? SI_NO_OPTIONS
              : campo === "estado"
                ? ESTADO_OPTIONS
              : campo === "estado_contratacion_director" || campo === "estado_contratacion_evaluador"
                ? ESTADOS_CONTRATACION_OPTIONS
               : []
      })),
    []
  );

  const maestriaActual = String(formData.maestria ?? "").trim().toUpperCase();
  const mostrarRolesExtendidos = MAESTRIAS_ROLES_EXTENDIDOS.has(maestriaActual);

  const secciones = useMemo(
    () => [
      {
        id: "datos-estudiante",
        titulo: "Datos del estudiante",
        campos: ["maestria", "fecha_inicio", "fecha_fin", "cohorte", "cedula", "estudiante"]
      },
      {
        id: "tema-principal",
        titulo: "Tema y roles principales",
        campos: [
          "tema",
          "director",
          "estado_contratacion_director",
          "evaluador",
          "estado_contratacion_evaluador"
        ]
      },
      {
        id: "roles-adicionales",
        titulo: "Roles adicionales",
        campos: ["tutor", "cotutor", "evaluador1", "evaluador2"],
        visible: mostrarRolesExtendidos
      },
      {
        id: "resoluciones",
        titulo: "Resoluciones",
        campos: ["resol_ca", "fecha_resol_ca", "resol_cua", "fecha_resol_cua"]
      },
      {
        id: "seguimiento",
        titulo: "Seguimiento academico",
        campos: [
          "fecha_graduacion",
          "examen",
          "estado",
          "fecha_tentativa",
          "periodo_inicio",
          "pendiente_actualizacion"
        ]
      },
      {
        id: "mat-titulacion",
        titulo: "Materia de titulacion 2 y prorrogas",
        campos: ["fecha_mat2", "prorroga1", "prorroga2", "mat_estado"]
      },
      {
        id: "observaciones",
        titulo: "Observaciones finales",
        campos: ["materia_reprobada", "deudas"]
      }
    ],
    [mostrarRolesExtendidos]
  );

  const camposPorId = useMemo(
    () =>
      campos.reduce((acc, campo) => {
        acc[campo.id] = campo;
        return acc;
      }, {}),
    [campos]
  );

  const handleChange = (event) => {
    const { name, value } = event.target;
    let nextValue = value;

    if (name === "estudiante" || name === "tema") {
      nextValue = value.toUpperCase();
    }

    if (
      name === "estado" ||
      name === "examen" ||
      name === "prorroga1" ||
      name === "prorroga2" ||
      name === "pendiente_actualizacion" ||
      name === "estado_contratacion_director" ||
      name === "estado_contratacion_evaluador"
    ) {
      nextValue = value.toUpperCase();
    }

    setFormData((prevData) => {
      const nextData = {
        ...prevData,
        [name]: CAMPOS_DOCENTE.has(name) ? nextValue.toUpperCase() : nextValue
      };

      if (name === "maestria" && !MAESTRIAS_ROLES_EXTENDIDOS.has(String(nextValue).trim().toUpperCase())) {
        nextData.tutor = "";
        nextData.cotutor = "";
        nextData.evaluador1 = "";
        nextData.evaluador2 = "";
      }

      return nextData;
    });

    setErrores((prevErrores) => {
      if (!prevErrores[name]) {
        return prevErrores;
      }

      const nextErrores = { ...prevErrores };
      delete nextErrores[name];
      return nextErrores;
    });
  };

  const validarFormulario = () => {
    const nuevosErrores = {};

    campos.forEach((campo) => {
      const valor = formData[campo.id];
      const valorNormalizado = typeof valor === "string" ? valor.trim() : valor;

      if (campo.required && !valorNormalizado) {
        nuevosErrores[campo.id] = "Este campo es obligatorio.";
        return;
      }

      if (valorNormalizado && campo.options.length > 0 && !campo.options.includes(String(valorNormalizado))) {
        nuevosErrores[campo.id] = "Debes seleccionar una opcion valida de la lista.";
      }

      if (campo.isDocente && valorNormalizado && !docentesDisponibles.includes(valor)) {
        nuevosErrores[campo.id] = "Debes seleccionar un docente que exista en la lista interna.";
      }
    });

    if (modo === "crear" && cantidadEstudiantes === "2") {
      const estudiantePrincipal = String(formData.estudiante ?? "").trim().toUpperCase();
      const segundoEstudiante = String(estudianteSecundario ?? "").trim().toUpperCase();

      if (!segundoEstudiante) {
        nuevosErrores.estudianteSecundario = "Debes ingresar el nombre del segundo estudiante.";
      } else if (segundoEstudiante === estudiantePrincipal) {
        nuevosErrores.estudianteSecundario = "El segundo estudiante debe ser diferente al principal.";
      }

      if (!cohorteSecundaria) {
        nuevosErrores.cohorteSecundaria = "Debes seleccionar la cohorte del segundo estudiante.";
      }

      if (cedulaSecundaria && cedulaSecundaria === formData.cedula) {
        nuevosErrores.cedulaSecundaria = "La cedula del segundo estudiante debe ser diferente.";
      }
    }

    return nuevosErrores;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const nuevosErrores = validarFormulario();

    if (Object.keys(nuevosErrores).length > 0) {
      setErrores(nuevosErrores);
      return;
    }

    const payload =
      modo === "crear" && cantidadEstudiantes === "2"
        ? {
            ...formData,
            _bulkStudents: [
              {
                estudiante: String(formData.estudiante ?? "").trim().toUpperCase(),
                cedula: String(formData.cedula ?? "").trim(),
                cohorte: String(formData.cohorte ?? "").trim(),
                fecha_inicio: String(formData.fecha_inicio ?? "").trim(),
                fecha_fin: String(formData.fecha_fin ?? "").trim()
              },
              {
                estudiante: String(estudianteSecundario ?? "").trim().toUpperCase(),
                cedula: String(cedulaSecundaria ?? "").trim(),
                cohorte: String(cohorteSecundaria ?? "").trim(),
                fecha_inicio: String(fechaInicioSecundaria ?? "").trim(),
                fecha_fin: String(fechaFinSecundaria ?? "").trim()
              }
            ]
          }
        : {
            ...formData,
            anio: normalizarAnioRegistro(formData.anio)
          };

    await onSubmit(payload);
  };

  const renderCampo = (campo) => {
    if (!campo) {
      return null;
    }

    if (
      modo === "crear" &&
      ["estudiante", "cedula", "cohorte", "fecha_inicio", "fecha_fin"].includes(campo.id)
    ) {
      if (campo.id !== "estudiante") {
        return null;
      }

      return (
        <div key={campo.id} style={styles.studentGroup}>
          <label style={styles.field}>
            <span style={styles.label}>Cantidad de estudiantes</span>
            <select
              value={cantidadEstudiantes}
              onChange={(event) => {
                setCantidadEstudiantes(event.target.value);
                setErrores((prevErrores) => {
                  if (
                    !prevErrores.estudianteSecundario &&
                    !prevErrores.cedulaSecundaria &&
                    !prevErrores.cohorteSecundaria
                  ) {
                    return prevErrores;
                  }

                  const nextErrores = { ...prevErrores };
                  delete nextErrores.estudianteSecundario;
                  delete nextErrores.cedulaSecundaria;
                  delete nextErrores.cohorteSecundaria;
                  delete nextErrores.fechaInicioSecundaria;
                  delete nextErrores.fechaFinSecundaria;
                  return nextErrores;
                });
              }}
              style={styles.select}
            >
              <option value="1">1 estudiante</option>
              <option value="2">2 estudiantes</option>
            </select>
          </label>

          <label style={styles.field}>
            <span style={styles.label}>
              {obtenerEtiqueta(campo.id)}
              {campo.required ? " *" : ""}
            </span>
            <input
              type="text"
              name={campo.id}
              value={formData[campo.id]}
              onChange={handleChange}
              style={styles.input}
            />
            {errores[campo.id] && <span style={styles.error}>{errores[campo.id]}</span>}
          </label>

          <label style={styles.field}>
            <span style={styles.label}>Cedula</span>
            <input
              type="text"
              name="cedula"
              value={formData.cedula}
              onChange={handleChange}
              style={styles.input}
            />
            {errores.cedula && <span style={styles.error}>{errores.cedula}</span>}
          </label>

          <label style={styles.field}>
            <span style={styles.label}>Cohorte *</span>
            <select
              name="cohorte"
              value={formData.cohorte}
              onChange={handleChange}
              style={styles.select}
            >
              <option value="">Selecciona una opcion</option>
              {COHORTES_DISPONIBLES.map((opcion) => (
                <option key={`cohorte-pri-${opcion}`} value={opcion}>
                  {opcion}
                </option>
              ))}
            </select>
            {errores.cohorte && <span style={styles.error}>{errores.cohorte}</span>}
          </label>

          <label style={styles.field}>
            <span style={styles.label}>Fecha de inicio de maestria</span>
            <input
              type="date"
              name="fecha_inicio"
              value={formData.fecha_inicio}
              onChange={handleChange}
              style={styles.input}
            />
            {errores.fecha_inicio && <span style={styles.error}>{errores.fecha_inicio}</span>}
          </label>

          <label style={styles.field}>
            <span style={styles.label}>Fecha fin de maestria</span>
            <input
              type="date"
              name="fecha_fin"
              value={formData.fecha_fin}
              onChange={handleChange}
              style={styles.input}
            />
            {errores.fecha_fin && <span style={styles.error}>{errores.fecha_fin}</span>}
          </label>

          {cantidadEstudiantes === "2" && (
            <>
              <label style={styles.field}>
                <span style={styles.label}>Segundo estudiante *</span>
                <input
                  type="text"
                  value={estudianteSecundario}
                  onChange={(event) => setEstudianteSecundario(event.target.value.toUpperCase())}
                  style={styles.input}
                />
                {errores.estudianteSecundario && (
                  <span style={styles.error}>{errores.estudianteSecundario}</span>
                )}
              </label>

              <label style={styles.field}>
                <span style={styles.label}>Cedula segundo estudiante</span>
                <input
                  type="text"
                  value={cedulaSecundaria}
                  onChange={(event) => setCedulaSecundaria(event.target.value)}
                  style={styles.input}
                />
                {errores.cedulaSecundaria && (
                  <span style={styles.error}>{errores.cedulaSecundaria}</span>
                )}
              </label>

              <label style={styles.field}>
                <span style={styles.label}>Cohorte segundo estudiante *</span>
                <select
                  value={cohorteSecundaria}
                  onChange={(event) => setCohorteSecundaria(event.target.value)}
                  style={styles.select}
                >
                  <option value="">Selecciona una opcion</option>
                  {COHORTES_DISPONIBLES.map((opcion) => (
                    <option key={`cohorte-sec-${opcion}`} value={opcion}>
                      {opcion}
                    </option>
                  ))}
                </select>
                {errores.cohorteSecundaria && (
                  <span style={styles.error}>{errores.cohorteSecundaria}</span>
                )}
              </label>

              <label style={styles.field}>
                <span style={styles.label}>Fecha de inicio de maestria</span>
                <input
                  type="date"
                  value={fechaInicioSecundaria}
                  onChange={(event) => setFechaInicioSecundaria(event.target.value)}
                  style={styles.input}
                />
              </label>

              <label style={styles.field}>
                <span style={styles.label}>Fecha fin de maestria</span>
                <input
                  type="date"
                  value={fechaFinSecundaria}
                  onChange={(event) => setFechaFinSecundaria(event.target.value)}
                  style={styles.input}
                />
              </label>
            </>
          )}

          {cantidadEstudiantes === "2" && (
            <div style={styles.fullWidthField}>
              <span style={styles.helperText}>
                Se crearan dos filas en Google Sheets con el mismo tema y los mismos docentes.
              </span>
            </div>
          )}
        </div>
      );
    }

    const esCampoCompacto = campo.id === "materia_reprobada" || campo.id === "deudas";

    if (campo.multiline) {
      return (
        <label key={campo.id} style={styles.fullWidthField}>
          <span style={styles.label}>
            {campo.label}
            {campo.required ? " *" : ""}
          </span>
          <textarea
            name={campo.id}
            value={formData[campo.id]}
            onChange={handleChange}
            rows={esCampoCompacto ? 2 : 4}
            style={{
              ...styles.textarea,
              ...(campo.id === "tema" ? styles.uppercaseInput : {}),
              ...(esCampoCompacto ? styles.textareaCompact : {})
            }}
          />
          {errores[campo.id] && <span style={styles.error}>{errores[campo.id]}</span>}
        </label>
      );
    }

    return (
      <label key={campo.id} style={styles.field}>
        <span style={styles.labelRow}>
          <span style={styles.label}>
            {campo.label}
            {campo.required ? " *" : ""}
          </span>
          {(campo.id === "estado" || campo.id === "estado_contratacion_director" || campo.id === "estado_contratacion_evaluador") && (
            <span
              title={
                campo.id === "estado"
                  ? "En este campo deben ingresar el estado actual del estudiante"
                  : campo.id === "estado_contratacion_director"
                    ? "Aqui deben registrar si el tutor ya esta contratado o si continua en proceso de contratacion."
                    : "Aqui deben registrar si el evaluador ya esta contratado o si continua en proceso de contratacion."
              }
              style={styles.infoIcon}
            >
              i
            </span>
          )}
        </span>
        {campo.options.length > 0 || campo.isDocente ? (
          <select
            name={campo.id}
            value={formData[campo.id]}
            onChange={handleChange}
            style={styles.select}
          >
            <option value="">Selecciona una opcion</option>
            {(campo.isDocente ? docentesDisponibles : campo.options).map((opcion) => (
              <option key={`${campo.id}-${opcion}`} value={opcion}>
                {opcion}
              </option>
            ))}
          </select>
        ) : (
          <input
            type={campo.type}
            name={campo.id}
            value={formData[campo.id]}
            onChange={handleChange}
            list={campo.isDocente ? `opciones-${campo.id}` : undefined}
            autoComplete={campo.isDocente ? "off" : undefined}
            style={{
              ...styles.input,
              ...(campo.id === "estudiante" ? styles.uppercaseInput : {})
            }}
          />
        )}
        {errores[campo.id] && <span style={styles.error}>{errores[campo.id]}</span>}
      </label>
    );
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <div>
            <h2 style={styles.title}>{modo === "editar" ? "Editar registro" : "Nuevo registro"}</h2>
            <p style={styles.subtitle}>
              {modo === "editar"
                ? "Actualiza la informacion del registro seleccionado."
                : "Completa los datos para agregar un nuevo estudiante."}
            </p>
          </div>

          <button type="button" onClick={onClose} style={styles.closeButton}>
            Cerrar
          </button>
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          <section style={styles.yearCard}>
            <div style={styles.sectionHeader}>
              <h3 style={styles.sectionTitle}>{"A\u00f1o de la propuesta de temas y temarios"}</h3>
            </div>

            <div style={styles.yearFieldWrap}>
              {renderCampo(camposPorId.anio)}
            </div>
          </section>

          {secciones
            .filter((seccion) => seccion.visible !== false)
            .map((seccion) => (
              <section key={seccion.id} style={styles.sectionCard}>
                <div style={styles.sectionHeader}>
                  <h3 style={styles.sectionTitle}>{seccion.titulo}</h3>
                </div>

                <div style={styles.grid}>
                  {seccion.campos.map((campoId) => renderCampo(camposPorId[campoId]))}
                </div>
              </section>
            ))}

          <div style={styles.actions}>
            <button type="button" onClick={onClose} style={styles.secondaryButton}>
              Cancelar
            </button>
            <button type="submit" style={styles.primaryButton} disabled={isSubmitting}>
              {isSubmitting ? "Guardando..." : modo === "editar" ? "Guardar cambios" : "Guardar registro"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ModalFormulario;

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: theme.colors.overlay,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px",
    zIndex: 1200
  },
  modal: {
    width: "min(1100px, 100%)",
    maxHeight: "90vh",
    overflowY: "auto",
    background: theme.colors.panelBg,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: "18px",
    padding: "24px",
    boxShadow: "0 24px 80px rgba(15, 23, 42, 0.18)"
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "16px",
    marginBottom: "20px"
  },
  title: {
    margin: 0,
    color: theme.colors.text
  },
  subtitle: {
    margin: "8px 0 0",
    color: theme.colors.subtext
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "20px"
  },
  sectionCard: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    padding: "18px",
    borderRadius: "16px",
    background: "#fbfcfd",
    border: `1px solid ${theme.colors.border}`
  },
  yearCard: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    padding: "18px",
    borderRadius: "16px",
    background: "#f5f9ff",
    border: `1px solid ${theme.colors.border}`
  },
  sectionHeader: {
    display: "flex",
    flexDirection: "column"
  },
  sectionTitle: {
    margin: 0,
    color: theme.colors.text,
    fontSize: "18px"
  },
  yearFieldWrap: {
    maxWidth: "260px"
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "16px"
  },
  studentGroup: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: "16px",
    padding: "16px",
    borderRadius: "14px",
    background: "#f8fafc",
    border: `1px solid ${theme.colors.border}`,
    gridColumn: "1 / -1"
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: "8px"
  },
  fullWidthField: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    gridColumn: "1 / -1"
  },
  label: {
    color: theme.colors.subtext,
    fontSize: "14px",
    fontWeight: 600
  },
  labelRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px"
  },
  infoIcon: {
    width: "18px",
    height: "18px",
    borderRadius: "999px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: theme.colors.secondary,
    color: theme.colors.primary,
    fontSize: "12px",
    fontWeight: 700,
    cursor: "help",
    border: `1px solid ${theme.colors.border}`
  },
  input: {
    background: "#f8fbff",
    border: `1px solid ${theme.colors.border}`,
    borderRadius: "10px",
    padding: "12px 14px",
    color: theme.colors.text,
    outline: "none"
  },
  select: {
    background: "#f8fbff",
    border: `1px solid ${theme.colors.border}`,
    borderRadius: "10px",
    padding: "12px 14px",
    color: theme.colors.text,
    outline: "none"
  },
  textarea: {
    background: "#f8fbff",
    border: `1px solid ${theme.colors.border}`,
    borderRadius: "10px",
    padding: "12px 14px",
    color: theme.colors.text,
    resize: "vertical",
    outline: "none",
    fontFamily: "inherit"
  },
  textareaCompact: {
    minHeight: "72px"
  },
  uppercaseInput: {
    textTransform: "uppercase"
  },
  error: {
    color: "#b42318",
    fontSize: "12px",
    fontWeight: 600
  },
  helperText: {
    color: theme.colors.subtext,
    fontSize: "12px",
    lineHeight: 1.5
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px"
  },
  closeButton: {
    background: "transparent",
    border: `1px solid ${theme.colors.border}`,
    color: theme.colors.text,
    borderRadius: "10px",
    padding: "10px 14px",
    cursor: "pointer"
  },
  secondaryButton: {
    background: "transparent",
    border: `1px solid ${theme.colors.border}`,
    color: theme.colors.text,
    borderRadius: "10px",
    padding: "12px 16px",
    cursor: "pointer"
  },
  primaryButton: {
    background: `linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.headerEnd})`,
    border: "none",
    color: "#fff",
    borderRadius: "10px",
    padding: "12px 16px",
    cursor: "pointer",
    fontWeight: 700
  }
};
